import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useTheme2027 } from "../styles/theme2027";
import "./navTabs.css";

// The page reserves `topGap` + the track for the pinned strip. A 2027 page passes its own card gap, which its spacer
// repeats below the strip (Phil 2026-10-21); pages still under the old banner keep 8.
export const NAV_TABS_GAP = 8;
export const NAV_TABS_TRACK = 40;

const INSET = 3;

// SCO-1870: under native chrome (headerHeight param → --sh-chrome-top, set at boot in index.jsx) the strip parks below
// the header and dodges on scroll: hidden scrolling down, back on any scroll up, always shown near the top. Mirrors the
// native header fade rules (12px rest / 3px jitter).
export function useNavDodge() {
  const [dodged, setDodged] = useState(false);
  useEffect(() => {
    if (!document.documentElement.style.getPropertyValue("--sh-chrome-top")) return undefined;
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      if (y <= 12) setDodged(false);
      else if (Math.abs(dy) >= 3) setDodged(dy > 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return dodged;
}

// Page tab strip (Design Guide 2027): floating segmented control, icon + label.
// items: [{ path, name, Icon }]; `disabled` keeps the tabs visible but inert. With `onChange` the tabs are buttons
// for in-page state instead of routes: items [{ key, name, Icon }], `value` = the active key (FAT's Find / My tournaments).
// `gutter` lines the strip up with a page whose cards sit off the 16px default (FAT: 10).
export default function NavTabs({ items, disabled, dodged, label, value, onChange, className, gutter, topGap = NAV_TABS_GAP }) {
  const t = useTheme2027();
  const { pathname } = useLocation();
  const active = onChange
    ? items.findIndex((item) => item.key === value)
    : items.findIndex((item) => matchPath({ path: `/${item.path}`, end: true }, pathname));
  // Equal tabs, but a long translated label can take more room, so the thumb is measured.
  const trackRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  const names = items.map((item) => item.name).join("|");
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => {
      const tab = track.querySelectorAll(".sh27-nav-tab")[active];
      setThumb(tab ? { left: tab.offsetLeft, width: tab.offsetWidth } : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    track.querySelectorAll(".sh27-nav-tab").forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
  }, [active, names]);
  // No slide on first paint: the transition switches on once a thumb has been placed.
  const placed = useRef(false);
  useLayoutEffect(() => {
    if (thumb) placed.current = true;
  }, [thumb]);

  return (
    <nav className={[dodged && "navDodge", className].filter(Boolean).join(" ")} aria-label={label}>
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: `${topGap}px ${gutter ?? t.gutter}px 0`,
          boxSizing: "border-box",
        }}
      >
        <div
          ref={trackRef}
          className="sh27-nav"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "stretch",
            minHeight: NAV_TABS_TRACK,
            padding: INSET,
            boxSizing: "border-box",
            borderRadius: t.radius.button,
            background: t.navTrack,
            border: `1px solid ${t.line}`,
            boxShadow: t.navShadow,
            fontFamily: t.font,
          }}
        >
          {thumb && (
            <span
              aria-hidden="true"
              className={placed.current ? "sh27-nav-thumb sh27-nav-slide" : "sh27-nav-thumb"}
              style={{
                position: "absolute",
                top: INSET,
                bottom: INSET,
                left: 0,
                width: thumb.width,
                transform: `translateX(${thumb.left}px)`,
                borderRadius: t.radius.button - INSET,
                background: t.segOn,
                boxShadow: t.segShadow,
              }}
            />
          )}
          {items.map(({ path, key, name, Icon }, index) => {
            const on = index === active;
            const Tab = onChange ? "button" : Link;
            const tabProps = onChange
              ? { type: "button", onClick: () => !disabled && onChange(key), "aria-pressed": on }
              : { to: disabled ? "#" : path, "aria-current": on ? "page" : undefined };
            return (
              <Tab
                key={(onChange ? key : path) + "-" + index}
                {...tabProps}
                className="sh27-nav-tab"
                style={{
                  position: "relative",
                  flex: "1 1 0",
                  minWidth: "min-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  textDecoration: "none",
                  lineHeight: 1.1,
                  fontWeight: on ? 700 : 600,
                  color: on ? t.text : t.muted,
                  ...(onChange && { border: 0, margin: 0, background: "none", fontFamily: "inherit", cursor: "pointer" }),
                }}
              >
                {Icon && (
                  <span
                    aria-hidden="true"
                    className={on ? "sh27-nav-icon sh27-nav-icon-on" : "sh27-nav-icon"}
                  >
                    <Icon size="100%" color={on ? t.red : t.muted} />
                  </span>
                )}
                <span style={{ overflowWrap: "break-word" }}>{name}</span>
              </Tab>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
